import { untrack, type Accessor } from 'solid-js'
import { platform } from '~/lib/params'
import type { StarterPack } from '~/services/starter-pack'
import type { ShopBalances } from '~/store/session-ledger'
import { iapPurchaseAdapter } from './purchase-flow/adapters'
import { PurchaseFlow } from './purchase-flow/surface'
import {
  createStarterPackRevealArt,
  StarterPackRewardReveal,
} from './starter-pack-reward-reveal'

// Buying the starter pack (GLOSSARY.md "Starter pack (新手禮包)"): the Shop
// Time Crystal purchase's IAP orchestration with no web confirmation — the
// flow submits as it mounts, behind the IAP processing overlay. Whatever
// surface started it stays beneath: a retryable error or a cancellation
// unmounts the flow (onCancel) and leaves the user there; a commit replaces
// it with the multi-item Reward reveal, whose last Claim completes the flow.
export function StarterPackPurchaseFlow(props: {
  balances: Accessor<ShopBalances | undefined>
  credit: (currency: keyof ShopBalances, amount: number) => void
  // A land skin's icon wherever Shop's catalogue holds it, for its page's art.
  landSkinIconUrl: (gid: number) => string | undefined
  onCancel: () => void
  onCommitted: (pack: StarterPack) => void
  onComplete: () => void
  pack: StarterPack
  userId: number
}) {
  // The reveal's art resolves while the purchase runs. The pack is fixed for
  // the flow's life: the shell mounts one flow per purchase.
  const art = untrack(() =>
    createStarterPackRevealArt(props.pack, {
      landSkinIconUrl: props.landSkinIconUrl,
    }),
  )
  return (
    <PurchaseFlow
      adapter={iapPurchaseAdapter({
        closeTransactionFailureEvent:
          'shop_starter_pack_close_transaction_failed',
        deliveryTimeoutEvent: 'shop_starter_pack_delivery_timeout',
        platform,
        product: () => ({
          productId: props.pack.product.id,
          sku: props.pack.sku,
        }),
        userId: () => props.userId,
      })}
      confirmation={null}
      onCancel={() => props.onCancel()}
      onCommitted={() => props.onCommitted(props.pack)}
      reveal={() => (
        <StarterPackRewardReveal
          art={art}
          balances={props.balances}
          credit={props.credit}
          onDone={() => props.onComplete()}
          pack={props.pack}
        />
      )}
      testIdPrefix="starter-pack-purchase"
    />
  )
}
